// ============================================
// useUnloadBeacon — pagehide/beforeunload safety net
// ============================================
// Extracted from VoiceBotPanel.tsx in Wave 6c of the widget refactor.
//
// If the user closes the tab mid-call (or while the post-call upload is
// still in flight), this hook fires a single `navigator.sendBeacon` so
// the server still gets the transcript + recording metadata.
//
// Behavior preserved exactly from the original inline useEffect:
//   - Listens on both `pagehide` (mobile-reliable) and `beforeunload`
//     (desktop, also shows the native "Leave site?" dialog).
//   - Skips firing if the session is inactive AND no post-call work is
//     pending, OR if the transcript has already been sent.
//   - Sets `transcriptSentRef.current = true` after firing so the second
//     event in the (beforeunload → pagehide) pair is a no-op.
//   - Effect re-attaches when `isSessionActive` changes (matching the
//     original `[isSessionActive]` dependency list).
//
// All mutable state stays where it was — refs are passed in and read
// live inside the listener; the hook owns no state of its own.

import { MutableRefObject, useEffect } from 'react'
import { sendTranscriptBeacon } from '../api/voice-bot-client'
import { trySendUnloadBeacon } from '../utils/unload-beacon-delivery'

interface RecordingData {
  url: string | null
  storagePath: string | null
  size: number
  type: string
}

interface BotConfigSnapshot {
  botName?: string
  companyName?: string
  propertyCode?: string | null
  recordingsFolderId?: string | null
  transcriptsFolderId?: string | null
  contactFormTiming?: string
  [key: string]: unknown
}

export interface UnloadBeaconOptions {
  isSessionActive: boolean
  postCallInProgressRef: MutableRefObject<boolean>
  transcriptSentRef: MutableRefObject<boolean>
  sessionStartTime: MutableRefObject<number | null>
  sessionDataRef: MutableRefObject<{ sessionId?: string; customerId?: string } | null>
  transcriptMessagesRef: MutableRefObject<unknown[]>
  bookingConfirmedRef: MutableRefObject<boolean>
  scheduledMeetingRef: MutableRefObject<unknown>
  contactInfoRef: MutableRefObject<unknown>
  configRef: MutableRefObject<BotConfigSnapshot>
  selectedLanguageRef: MutableRefObject<string>
  recordingDataRef: MutableRefObject<RecordingData>
  tokensUsedRef: MutableRefObject<number>
  contactInfoSourceRef: MutableRefObject<unknown>
  reachedSchedulingRef: MutableRefObject<boolean>
  chunkUploadActiveRef: MutableRefObject<boolean>
  chunkIndexRef: MutableRefObject<number>
}

export const useUnloadBeacon = (options: UnloadBeaconOptions): void => {
  const {
    isSessionActive,
    postCallInProgressRef,
    transcriptSentRef,
    sessionStartTime,
    sessionDataRef,
    transcriptMessagesRef,
    bookingConfirmedRef,
    scheduledMeetingRef,
    contactInfoRef,
    configRef,
    selectedLanguageRef,
    recordingDataRef,
    tokensUsedRef,
    contactInfoSourceRef,
    reachedSchedulingRef,
    chunkUploadActiveRef,
    chunkIndexRef,
  } = options

  useEffect(() => {
    const handlePageHide = () => {
      // Fire if session is active OR post-call processing is in progress (finalization/upload)
      // isSessionActive is set to false immediately for UI, but postCallInProgressRef
      // stays true until transcript is actually sent to the server.
      if ((!isSessionActive && !postCallInProgressRef.current) || transcriptSentRef.current) return

      const duration = sessionStartTime.current
        ? Math.round((Date.now() - sessionStartTime.current) / 1000)
        : 0

      const beaconPayload = {
        sessionId: sessionDataRef.current?.sessionId,
        transcript: transcriptMessagesRef.current,
        whisperTranscript: null,
        duration,
        outcome: bookingConfirmedRef.current ? 'scheduled' : (transcriptMessagesRef.current.length > 2 ? 'question_answered' : 'unknown'),
        scheduledMeeting: bookingConfirmedRef.current ? scheduledMeetingRef.current : null,
        contactInfo: contactInfoRef.current || null,
        botInfo: {
          botName: configRef.current.botName,
          companyName: configRef.current.companyName,
          language: selectedLanguageRef.current,
          propertyCode: configRef.current.propertyCode || null,
          recordingsFolderId: configRef.current.recordingsFolderId || null,
          transcriptsFolderId: configRef.current.transcriptsFolderId || null
        },
        pageContext: {
          url: window.location.href,
          referrer: document.referrer || null,
          userAgent: navigator.userAgent
        },
        recordingUrl: recordingDataRef.current.url,
        recordingStoragePath: recordingDataRef.current.storagePath,
        recordingSize: recordingDataRef.current.size,
        recordingType: recordingDataRef.current.type,
        customerId: sessionDataRef.current?.customerId,
        tokensUsed: tokensUsedRef.current,
        abTesting: {
          contactFormTiming: configRef.current.contactFormTiming || 'none',
          contactInfoCollected: !!contactInfoRef.current,
          contactInfoSource: contactInfoSourceRef.current,
          reachedScheduling: reachedSchedulingRef.current,
          bookingCompleted: bookingConfirmedRef.current,
          isAnonymous: !contactInfoRef.current
        },
        _beacon: true, // Flag so server knows this came from pagehide
        // Chunk upload info — server can auto-finalize if chunks were uploaded
        _chunkedUpload: chunkUploadActiveRef.current || chunkIndexRef.current > 0,
        _totalChunksUploaded: chunkIndexRef.current
      }

      const accepted = trySendUnloadBeacon(
        () => sendTranscriptBeacon(beaconPayload),
        transcriptSentRef,
      )
      if (accepted) {
        console.log('🚨 Beacon fired — tab closing before transcript was sent')
      } else {
        console.warn('⚠️ Browser rejected transcript beacon; pagehide will retry if available')
      }
    }

    // pagehide is more reliable than beforeunload on mobile
    window.addEventListener('pagehide', handlePageHide)

    // beforeunload: show browser confirmation dialog + fire beacon as fallback
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if ((!isSessionActive && !postCallInProgressRef.current) || transcriptSentRef.current) return
      // Show native "Leave site?" dialog to give upload time to complete
      e.preventDefault()
      // Also fire beacon in case user confirms leaving
      handlePageHide()
    }
    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => {
      window.removeEventListener('pagehide', handlePageHide)
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [isSessionActive])
}
